import type { FormGroupSize } from '@ksyos/design-system/dist/components/form-group/form-group.types';
import { FormGroup, FormGroupContext, FormParagraph } from '@ksyos/design-system';
import { useMemo } from 'react';
import type { Gender } from '@common/enums';
import { getLanguage } from '@common/translate/translate';
import { TcohProcedureType } from '../../../../common/enums/tcohProcedureType';
import { NullableFormattedDatetime } from '../../elements/NullableFormattedDatetime';
import { NullableStringBirthDate } from '../../elements/NullableStringBirthDate';

interface IHolterInformationProps {
    batteryChargePercentage?: string;
    birthDate?: string;
    configurationDatetime?: string;
    firmwareVersion?: string;
    gender?: Gender | null;
    patientName?: string;
    procedureType?: TcohProcedureType | string;
    recorderId?: string;
}

export function HolterInformation(props: Readonly<IHolterInformationProps>) {
    const translate = getLanguage();
    const {
        batteryChargePercentage,
        birthDate,
        configurationDatetime,
        firmwareVersion,
        patientName,
        gender,
        procedureType,
        recorderId,
    } = props;
    const styling = useMemo(() => ({ size: 'medium' as FormGroupSize }), []);

    return (
        <div className="col-sm-24 col-md-12">
            <FormGroupContext.Provider value={styling}>
                <FormGroup label={`${translate.patient.patient}:`}>
                    <FormParagraph style={{ fontWeight: 'bold' }}>{patientName ?? '-'}</FormParagraph>
                    <FormParagraph style={{ fontWeight: 'bold' }}>
                        {gender ? translate.genderEnum[gender] : '-'}
                    </FormParagraph>
                    <FormParagraph style={{ fontWeight: 'bold' }}>
                        {birthDate ? <NullableStringBirthDate value={birthDate} /> : '-'}
                    </FormParagraph>
                </FormGroup>

                <FormGroup label={`${translate.common.configuredOn}:`}>
                    <FormParagraph>
                        {configurationDatetime ? <NullableFormattedDatetime value={configurationDatetime} /> : '-'}
                    </FormParagraph>
                </FormGroup>

                <FormGroup label={`${translate.common.recordingType}:`}>
                    <FormParagraph>{getProcedureType()}</FormParagraph>
                </FormGroup>

                <FormGroup label={`${translate.holter.recorderId}:`}>
                    <FormParagraph style={{ fontWeight: 'bold' }}>
                        {recorderId ?? '-'} {firmwareVersion ? `(${firmwareVersion})` : ''}
                    </FormParagraph>
                </FormGroup>

                {batteryChargePercentage && (
                    <FormGroup label={`${translate.holter.batteryPercentage}:`}>
                        <FormParagraph style={{ fontWeight: 'bold' }}>{`${batteryChargePercentage}%`}</FormParagraph>
                    </FormGroup>
                )}
            </FormGroupContext.Provider>
        </div>
    );

    function getProcedureType() {
        if (!procedureType) {
            return '-';
        }
        if ([TcohProcedureType.eventEcg, TcohProcedureType.holter].includes(procedureType as TcohProcedureType)) {
            return translate.tcohProcedureTypeEnum[procedureType as TcohProcedureType];
        } else {
            return procedureType;
        }
    }
}
